import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useNavigate } from "react-router-dom"
import { 
  Calendar, 
  MapPin, 
  Users, 
  Building2, 
  Video, 
  User, 
  Mail, 
  Globe, 
  Phone,
  Heart,
  Target,
  DollarSign,
  Languages,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from "lucide-react"
import { Event } from "@/types/events"
import { format, isToday, isYesterday, isTomorrow, parseISO } from "date-fns"
import { getStatusColor, formatStatus } from "@/lib/utils/status"
import { Badge } from "@/components/ui/badge"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"

interface PaginationInfo {
  page: number
  limit: number
  total: number
  totalPages: number
}

interface UpcomingEventsTableProps {
  events: Event[]
  profile: any | null
  session: any | null
  pagination?: PaginationInfo
  onPageChange?: (page: number) => void
}

export const UpcomingEventsTable = ({ events, profile, session, pagination, onPageChange }: UpcomingEventsTableProps) => {
  const navigate = useNavigate()

  const formatSmartDate = (date: string, time: string) => {
    try {
      // Properly combine date and time before parsing
      let eventDate: Date;
      try {
          eventDate = parseISO(date);
      } catch (error) {
        console.error("Error parsing event date/time:", error);
        // Fallback to current date if parsing fails
        eventDate = new Date();
      }
      
      let dateStr = "";

      if (isToday(eventDate)) {
        dateStr = "Today";
      } else if (isTomorrow(eventDate)) {
        dateStr = "Tomorrow";
      } else if (isYesterday(eventDate)) {
        dateStr = "Yesterday";
      } else {
        // Get the timezone name using Intl.DateTimeFormat
        const timezoneName = new Intl.DateTimeFormat('en', {
          timeZoneName: 'short'
        }).formatToParts(eventDate).find(part => part.type === 'timeZoneName')?.value || '';
        
        dateStr = `${format(eventDate, "EEEE, MMM d, yyyy")} (${timezoneName})`;
      }

      const timeStr = format(eventDate, "h:mm a");
      return `${dateStr} at ${timeStr}`;
    } catch (error) {
      console.error("Error parsing event date/time:", error);
      return `${date} at ${time}`;
    }
  }

  const getInitials = (name: string) => {
    if (!name) return "?"
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[30%]">Event Details</TableHead>
            <TableHead className="w-[25%]">Organizer</TableHead>
            <TableHead className="w-[25%]">Location & Time</TableHead>
            <TableHead className="w-[20%]">Status & Participants</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events?.map((event: any) => (
            <TableRow 
              key={event.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => navigate(`/dashboard/events/${event.id}`)}
            >
              <TableCell>
                <div className="space-y-1">
                  <div className="font-medium">{event.event_name}</div>
                  {event.opportunity_catalog && (
                    <div className="text-sm text-muted-foreground">
                      {event.opportunity_catalog.duration} • {event.opportunity_catalog.format}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center space-x-4">
                  {event.corporate && profile?.user_type !== 'company' && (
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <button className="flex items-center space-x-2 hover:text-primary">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={event.corporate.profile_photo_url || undefined} />
                            <AvatarFallback>
                              {getInitials(event.corporate.org_name === 'Individual' 
                                ? `${event.corporate.poc_first_name} ${event.corporate.poc_last_name}`
                                : event.corporate.org_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-left">
                            <div className="font-medium">
                              {event.corporate.org_name === 'Individual' 
                                ? `${event.corporate.poc_first_name} ${event.corporate.poc_last_name}`
                                : event.corporate.org_name}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {event.corporate.org_name === 'Individual' ? 'Individual Volunteer' : 'Corporate Partner'}
                            </div>
                          </div>
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-80">
                        <div className="flex justify-between space-x-4">
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={event.corporate.profile_photo_url || undefined} />
                            <AvatarFallback>
                              {getInitials(event.corporate.org_name === 'Individual' 
                                ? `${event.corporate.poc_first_name} ${event.corporate.poc_last_name}`
                                : event.corporate.org_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="space-y-1 flex-1">
                            <h4 className="text-sm font-semibold">
                              {event.corporate.org_name === 'Individual' 
                                ? `${event.corporate.poc_first_name} ${event.corporate.poc_last_name}`
                                : event.corporate.org_name}
                            </h4>
                            
                            <div className="text-sm pt-2 space-y-2">
                              {event.corporate.email && (
                                <div className="flex items-center">
                                  <Mail className="h-3 w-3 mr-2" />
                                  <a 
                                    href={`mailto:${event.corporate.email}`}
                                    className="text-primary hover:underline"
                                  >
                                    {event.corporate.email}
                                  </a>
                                </div>
                              )}
                              {event.corporate.phone && (
                                <div className="flex items-center text-muted-foreground">
                                  <Phone className="h-3 w-3 mr-2" />
                                  {event.corporate.isd_code} {event.corporate.phone}
                                </div>
                              )}
                              {event.corporate.cause_areas && event.corporate.cause_areas.length > 0 && (
                                <div className="flex flex-wrap gap-1 pt-2">
                                  {event.corporate.cause_areas.map((area) => (
                                    <Badge key={area} variant="secondary" className="text-xs">
                                      {area}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  )}
                  {event.npo && profile?.user_type !== 'nonprofit' && (
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <button className="flex items-center space-x-2 hover:text-primary">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={event.npo.logo_url || undefined} />
                            <AvatarFallback>{getInitials(event.npo.org_name)}</AvatarFallback>
                          </Avatar>
                          <div className="text-left">
                            <div className="font-medium">{event.npo.org_name}</div>
                            <div className="text-sm text-muted-foreground">Non-Profit Organization</div>
                          </div>
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-96">
                        <Tabs defaultValue="info">
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="info">Info</TabsTrigger>
                            <TabsTrigger value="impact">Impact Areas</TabsTrigger>
                          </TabsList>
                          <TabsContent value="info" className="space-y-4">
                            <div className="flex justify-between space-x-4">
                              <Avatar className="h-12 w-12">
                                <AvatarImage src={event.npo.logo_url || undefined} />
                                <AvatarFallback>{getInitials(event.npo.org_name)}</AvatarFallback>
                              </Avatar>
                              <div className="space-y-1 flex-1">
                                <h4 className="text-sm font-semibold">{event.npo.org_name}</h4>
                                <div className="text-sm text-muted-foreground line-clamp-2">
                                  {event.npo.description}
                                </div>
                                
                                <div className="text-sm pt-2 space-y-2">
                                  {event.npo.email && (
                                    <div className="flex items-center">
                                      <Mail className="h-3 w-3 mr-2" />
                                      <a 
                                        href={`mailto:${event.npo.email}`}
                                        className="text-primary hover:underline"
                                      >
                                        {event.npo.email}
                                      </a>
                                    </div>
                                  )}
                                  {event.npo.phone && (
                                    <div className="flex items-center text-muted-foreground">
                                      <Phone className="h-3 w-3 mr-2" />
                                      {event.npo.isd_code} {event.npo.phone}
                                    </div>
                                  )}
                                  {event.npo.website && (
                                    <div className="flex items-center">
                                      <Globe className="h-3 w-3 mr-2" />
                                      <a 
                                        href={event.npo.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline"
                                      >
                                        Website
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </TabsContent>
                          <TabsContent value="impact" className="space-y-4">
                            <div className="space-y-4">
                              {event.npo.focus_areas && event.npo.focus_areas.length > 0 && (
                                <div className="space-y-2">
                                  <div className="flex items-center text-sm font-medium">
                                    <Heart className="h-3 w-3 mr-2" />
                                    Focus Areas
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {event.npo.focus_areas.map((area) => (
                                      <Badge key={area} variant="secondary" className="text-xs">
                                        {area}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {event.npo.sdg_alignment && event.npo.sdg_alignment.length > 0 && (
                                <div className="space-y-2">
                                  <div className="flex items-center text-sm font-medium">
                                    <Target className="h-3 w-3 mr-2" />
                                    SDG Alignment
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {event.npo.sdg_alignment.map((sdg) => (
                                      <Badge key={sdg} variant="secondary" className="text-xs">
                                        {sdg}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </TabsContent>
                        </Tabs>
                      </HoverCardContent>
                    </HoverCard>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  <div className="flex items-center text-sm">
                    <Calendar className="h-3 w-3 mr-1 text-gray-500" />
                    <span>{formatSmartDate(event.event_date, event.event_time)}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    {event.opportunity_catalog?.format === 'Virtual' ? (
                      <Video className="h-3 w-3 mr-1 text-gray-500" />
                    ) : event.opportunity_catalog?.format === 'In-Office' ? (
                      <Building2 className="h-3 w-3 mr-1 text-gray-500" />
                    ) : (
                      <MapPin className="h-3 w-3 mr-1 text-gray-500" />
                    )}
                    {event.location || event.opportunity_catalog?.format_details?.startsWith('http') ? (
                      <a 
                        href={event.location || event.opportunity_catalog?.format_details}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View location
                      </a>
                    ) : (
                      <span>
                        {event.opportunity_catalog?.format}
                        {event.opportunity_catalog?.format_details && (
                          <span className="text-muted-foreground ml-1">
                            - {event.opportunity_catalog.format_details}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 mt-2 text-sm">
                    {event.host_included && (
                      <div className="flex items-center text-muted-foreground">
                        <User className="h-3 w-3 mr-1 text-gray-500" />
                        Host Included
                      </div>
                    )}
                    {event.pay_later && (
                      <div className="flex items-center text-muted-foreground">
                        <DollarSign className="h-3 w-3 mr-1 text-gray-500" />
                        PayLater
                      </div>
                    )}
                    {event.preferred_language && (
                      <div className="flex items-center text-muted-foreground">
                        <Languages className="h-3 w-3 mr-1 text-gray-500" />
                        {event.preferred_language}
                      </div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-2">
                  <Badge variant="outline" className={getStatusColor(event.status)}>
                    {formatStatus(event.status)}
                  </Badge>
                  <div className="flex items-center text-sm">
                    <Users className="h-3 w-3 mr-1 text-gray-500" />
                    <span>
                      {event.event_participants?.length || 0} / {event.expected_participants || event.opportunity_catalog?.max_volunteers || 0}
                    </span>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pagination && (
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <div className="flex-1 text-sm text-muted-foreground">
            Showing <span className="font-medium">{events.length}</span> of{" "}
            <span className="font-medium">{pagination.total}</span> events
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange?.(1)}
              disabled={pagination.page <= 1}
              className="h-8 w-8"
            >
              <span className="sr-only">First page</span>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange?.(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="h-8 w-8"
            >
              <span className="sr-only">Previous page</span>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange?.(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="h-8 w-8"
            >
              <span className="sr-only">Next page</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange?.(pagination.totalPages)}
              disabled={pagination.page >= pagination.totalPages}
              className="h-8 w-8"
            >
              <span className="sr-only">Last page</span>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}